import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react'

const PackedAlphaVideo = lazy(() => import('./PackedAlphaVideo'))

// Approved video design. Set false to restore the saved static homepage.
// ?hero=default / ?hero=video remains available for comparison.
const defaultVideoPreview = true

export function HomeHeroMedia() {
  const [preview, setPreview] = useState(defaultVideoPreview)
  const [motion, setMotion] = useState(false)
  const [playing, setPlaying] = useState(true)
  const [ready, setReady] = useState(false)
  const [source, setSource] = useState('/videos/intro-loop-960-v1.webm')
  const [packed, setPacked] = useState(false)
  const video = useRef(null)
  const showAnimation = useCallback(() => setReady(true), [])
  const showStill = useCallback(() => { setReady(false); setMotion(false) }, [])

  useEffect(() => {
    const choice = new URLSearchParams(window.location.search).get('hero')
    if (choice === 'default' || choice === 'video') setPreview(choice === 'video')
    const appleBrowser = /iPad|iPhone|iPod/.test(navigator.userAgent)
      || (navigator.vendor === 'Apple Computer, Inc.')
    setPacked(appleBrowser || new URLSearchParams(window.location.search).get('transparency') === 'packed')
    setSource(matchMedia('(max-width: 560px)').matches ? '/videos/intro-loop-540-v1.webm' : '/videos/intro-loop-960-v1.webm')
    const preference = matchMedia('(prefers-reduced-motion: reduce)')
    const update = () => setMotion(!preference.matches && !navigator.connection?.saveData)
    update()
    preference.addEventListener('change', update)
    return () => preference.removeEventListener('change', update)
  }, [])

  useEffect(() => {
    const element = video.current
    if (!element) return
    const sync = () => {
      if (playing && !document.hidden) element.play().catch(error => {
        if (error.name === 'AbortError') return
        setReady(false)
        if (element.error || error.name === 'NotSupportedError') setPacked(true)
        else setPlaying(false)
      })
      else element.pause()
    }
    sync()
    document.addEventListener('visibilitychange', sync)
    return () => document.removeEventListener('visibilitychange', sync)
  }, [preview, motion, playing])

  return <div className="bw-logo-intro">
    <div className="bw-logo-image">
      <picture style={preview && motion && ready ? { opacity: 0 } : undefined}>
        <source media="(max-width: 560px)" srcSet={preview ? '/videos/intro-poster-540-v1.webp' : '/images/branding/logos/blazin-logo-540.webp'} />
        <img src={preview ? '/videos/intro-poster-960-v1.webp' : '/images/branding/logos/blazin-logo-960-v2.webp'} width="960" height="480" alt="Blazin Welding & Fabrication LLC" loading="eager" decoding="async" fetchPriority="high" />
      </picture>
      {preview && motion && (packed
        ? <Suspense fallback={null}><PackedAlphaVideo src={source.replace('intro-loop-', 'intro-packed-').replace('-v1.webm', '-v2.mp4')} playing={playing} onReady={showAnimation} onError={showStill} /></Suspense>
        : <video ref={video} src={source} className="bw-intro-video" style={{ opacity: ready ? 1 : 0 }} width="960" height="480" autoPlay muted loop playsInline preload="auto" aria-hidden="true" onPlaying={event => {
          // Verify decoded alpha before revealing it; codec support alone is insufficient.
          try {
            const probe = document.createElement('canvas')
            probe.width = probe.height = 1
            const context = probe.getContext('2d', { willReadFrequently: true })
            context.drawImage(event.currentTarget, 0, 0, 1, 1, 0, 0, 1, 1)
            if (context.getImageData(0, 0, 1, 1).data[3] > 8) { setReady(false); setPacked(true); return }
            showAnimation()
          } catch { setReady(false); setPacked(true) }
        }} onError={() => { setReady(false); setPacked(true) }} />)}
    </div>
    {preview && motion && <button type="button" className="bw-intro-toggle" onClick={() => setPlaying(value => !value)}>{playing ? 'Pause animation' : 'Play animation'}</button>}
  </div>
}
